import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button, Table, Form, Row, Col, FloatingLabel, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faEdit, faDownload } from "@fortawesome/free-solid-svg-icons";
import { FaEye } from "react-icons/fa";
import * as XLSX from 'xlsx';
import { Loading, AppPagination } from "../../components/Index.js";
import AppNav from "../../../header/AppNav";
import AlertModal from "../../../components/AlertModal";
import { PatientServices } from "../../../services/PatientService";
import { AuthService } from "../../../services/AuthService";
import { TalukServices } from "../../../services/Taluk";
import { PHCServices } from "../../../services/Phcs";
import { AshaBlockServices } from "../../../services/AshaBlocks";
import { AshaServices } from "../../../services/AshaService";

function PatientsHistory() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;
  
  const [selectedTaluk, setSelectedTaluk] = useState("");
  const [selectedPhc, setSelectedPhc] = useState("");
  const [selectedAshaBlock, setSelectedAshaBlock] = useState("");
  const [selectedAsha, setSelectedAsha] = useState("");
  const [selectedProblemType, setSelectedProblemType] = useState("");
  
  const [taluks, setTaluks] = useState([]);
  const [phcs, setPhcs] = useState([]);
  const [ashaBlocks, setAshaBlocks] = useState([]);
  const [ashas, setAshas] = useState([]);
  
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [modalData, setModalData] = useState({});
  
  const roleDetails = AuthService.getUserRole();
  const userRole = roleDetails?.role;
  const user = AuthService.getAuthEntity();
  
  useEffect(() => {
    const initialize = async () => {
      await initializeData();
      // Only fetch patients initially if not a Hospital user (Hospital users need to select problem type first)
      if (userRole !== "Hospital") {
        await fetchPatients();
      }
    };
    initialize();
  }, []);

  // Handle filter changes for Hospital users - Taluk dropdown
  useEffect(() => {
    if (userRole === "Hospital") {
      setSelectedPhc("");
      setSelectedAshaBlock("");
      setSelectedAsha("");
      
      if (selectedTaluk) {
        loadPhcsForTaluk(selectedTaluk);
        setAshaBlocks([]);
      } else {
        setPhcs([]);
        setAshaBlocks([]);
      }
    }
  }, [selectedTaluk]);

  // Handle filter changes for Hospital users - PHC dropdown
  useEffect(() => {
    if (userRole === "Hospital") {
      setSelectedAshaBlock("");
      setSelectedAsha("");
      
      if (selectedPhc) {
        loadAshaBlocksForPhc(selectedPhc);
      } else if (selectedTaluk) {
        const loadTalukAshaBlocks = async () => {
          try {
            const blocksRes = await AshaBlockServices.getAshaBlockByTalukId(selectedTaluk);
            setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
          } catch (error) {
            console.error("Error loading taluk asha blocks:", error);
          }
        };
        loadTalukAshaBlocks();
      } else {
        setAshaBlocks([]);
      }
    }
  }, [selectedPhc]);

  // Handle filter changes for Hospital users - AshaBlock dropdown
  useEffect(() => {
    if (userRole === "Hospital") {
      setSelectedAsha("");
    }
  }, [selectedAshaBlock]);
  
  useEffect(() => {
    if (userRole && (selectedTaluk || selectedPhc)) {
      fetchPatients();
    }
  }, [selectedTaluk, selectedPhc]);
  
  useEffect(() => {
    if (userRole) {
      fetchPatients();
    }
  }, [currentPage]);
  
  const initializeData = async () => {
    try {
      if (userRole === "Super Admins") {
        await loadAllFilters();
      } else if (userRole === "Taluk") {
        const talukId = user.talukDetails?.id;
        await loadPhcsForTaluk(talukId);

        const blocksRes = await AshaBlockServices.getAshaBlockByTalukId(talukId);
        setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));

        const ashasRes = await AshaServices.getAshaByTalukId(talukId);
        setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
      } else if (userRole === "PHC") {
        const phcId = user.phcDetails?.id;
        await loadAshaBlocksForPhc(phcId);

        const ashasRes = await AshaServices.getAshaByPhcId(phcId);
        setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
      } else if (userRole === "Asha Block") {
        await loadAshasForAshaBlock(user.ashaBlockDetails?.id);
      } else if (userRole === "Hospital") {
        await loadAllFilters();
      }
    } catch (error) {
      console.error("Error initializing data:", error);
    }
  };
  
  const loadAllFilters = async () => {
    try {

      const taluksRes = await TalukServices.getAllTaluks();
      setTaluks(Array.isArray(taluksRes) ? taluksRes : Object.values(taluksRes || {}));
      

      const phcsRes = await PHCServices.getAllPhcs();
      setPhcs(Array.isArray(phcsRes) ? phcsRes : Object.values(phcsRes || {}));

      const blocksRes = await AshaBlockServices.getAllAshaBlocks();
      setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
      

      const ashasRes = await AshaServices.getAllAshas();
      setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
    } catch (error) {
      console.error("Error loading filters:", error);
    }
  };
  
  const loadPhcsForTaluk = async (talukId) => {
    try {
      const phcsRes = await PHCServices.gePhcsByTalukId(talukId);
      setPhcs(Array.isArray(phcsRes) ? phcsRes : Object.values(phcsRes || {}));
    } catch (error) {
      console.error("Error loading PHCs:", error);
    }
  };
  
  const loadAshaBlocksForPhc = async (phcId) => {
    try {
      const blocksRes = await AshaBlockServices.geAshaBlockByPhcId(phcId);
      setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
    } catch (error) {
      console.error("Error loading Asha Blocks:", error);
    }
  };
  
  const loadAshasForAshaBlock = async (ashaBlockId) => {
    try {
      const ashasRes = await AshaServices.getAshaByAshaBlockId(ashaBlockId);
      setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
    } catch (error) {
      console.error("Error loading Ashas:", error);
      setAshas([]); 
    }
  };
  
  useEffect(() => {
    if (userRole === "Super Admins") {

      setSelectedPhc("");
      setSelectedAshaBlock("");
      setSelectedAsha("");
      
      if (selectedTaluk) {

        loadPhcsForTaluk(selectedTaluk);

        setAshaBlocks([]);
        setAshas([]);
      } else {
 
        loadAllFilters();
        setCurrentPage(0); 
      }
    }
  }, [selectedTaluk]);
  

  useEffect(() => {

    setSelectedAshaBlock("");
    setSelectedAsha("");
    
    if (selectedPhc) {
      loadAshaBlocksForPhc(selectedPhc);

      setAshas([]);
    } else if (userRole === "Super Admins" && !selectedTaluk) {

      const loadAllAshaBlocksAndAshas = async () => {
        try {
          const blocksRes = await AshaBlockServices.getAllAshaBlocks();
          setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
          const ashasRes = await AshaServices.getAllAshas();
          setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
        } catch (error) {
          console.error("Error loading all blocks and ashas:", error);
        }
      };
      loadAllAshaBlocksAndAshas();
    } else if (userRole === "Taluk" && !selectedPhc) {
    
      const talukId = user.talukDetails?.id;
      const loadTalukAshaBlocksAndAshas = async () => {
        try {
          const blocksRes = await AshaBlockServices.getAshaBlockByTalukId(talukId);
          setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
          const ashasRes = await AshaServices.getAshaByTalukId(talukId);
          setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
        } catch (error) {
          console.error("Error loading taluk blocks and ashas:", error);
        }
      };
      loadTalukAshaBlocksAndAshas();
    }
  }, [selectedPhc]);
  
  
  useEffect(() => {
    setSelectedAsha("");
    if (selectedAshaBlock) {

      setAshas([]);
      loadAshasForAshaBlock(selectedAshaBlock);
    } else {
      
      if (userRole === "Super Admins" && !selectedTaluk && !selectedPhc) {

        const loadAllAshas = async () => {
          try {
            const ashasRes = await AshaServices.getAllAshas();
            setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
          } catch (error) {
            console.error("Error loading all ashas:", error);
          }
        };
        loadAllAshas();
      } else if (userRole === "Taluk" && !selectedPhc) {
        const talukId = user.talukDetails?.id;
        const loadTalukAshas = async () => {
          try {
            const ashasRes = await AshaServices.getAshaByTalukId(talukId);
            setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
          } catch (error) {
            console.error("Error loading taluk ashas:", error);
          }
        };
        loadTalukAshas();
      } else if (userRole === "PHC" || (selectedPhc && !selectedAshaBlock)) {
        const phcId = selectedPhc || user.phcDetails?.id;
        const loadPhcAshas = async () => {
          try {
            const ashasRes = await AshaServices.getAshaByPhcId(phcId);
            setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
          } catch (error) {
            console.error("Error loading PHC ashas:", error);
          }
        };
        loadPhcAshas();
      }
    }
  }, [selectedAshaBlock]);
  
  // Add useEffect for selectedProblemType to trigger fetchPatients for Hospital users
  useEffect(() => {
    if (userRole === "Hospital") {
      fetchPatients();
    }
  }, [selectedProblemType]);
  
  const fetchPatients = async () => {
    try {
      setLoading(true);
      let response;
      
      if (userRole === "Super Admins") {
        if (selectedTaluk) {
          response = await PatientServices.getPatientsByTaluk(selectedTaluk, currentPage, pageSize);
        } else if (selectedPhc) {
          response = await PatientServices.getPatientsByPhc(selectedPhc, currentPage, pageSize);
        } else {
          response = await PatientServices.getAllPatients(currentPage, pageSize);
        }
      } else if (userRole === "Taluk") {
        const talukId = user.talukDetails?.id;
        if (selectedPhc) {
          response = await PatientServices.getPatientsByPhc(selectedPhc, currentPage, pageSize);
        } else {
          response = await PatientServices.getPatientsByTaluk(talukId, currentPage, pageSize);
        }
      } else if (userRole === "PHC") {
        const phcId = user.phcDetails?.id;
        response = await PatientServices.getPatientsByPhc(phcId, currentPage, pageSize);
      } else if (userRole === "Asha Block") {
        const ashaBlockId = user.ashaBlockDetails?.id;
        response = await PatientServices.getPatientsByAshaBlock(ashaBlockId, currentPage, pageSize);
      } else if (userRole === "Hospital") {
        const hospitalId = user.id;
        // Only fetch patients if a problem type is selected
        if (selectedProblemType) {
          response = await PatientServices.getPatientsByHospital(hospitalId, selectedProblemType, currentPage, pageSize);
        } else {
          // Don't fetch patients if no problem type is selected
          setPatients([]);
          setTotalPages(0);
          setTotalElements(0);
          setLoading(false);
          return;
        }
      }
      
      if (response?.data) {
        const responseData = response.data;
        
        // Check if the response has the expected structure
        if (responseData.patients) {
          setPatients(responseData.patients);
          setTotalPages(responseData.totalPages || 0);
          setTotalElements(responseData.totalElements || 0);
        } else if (Array.isArray(responseData)) {
          setPatients(responseData);
          setTotalPages(1);
          setTotalElements(responseData.length);
        } else if (responseData.content) {
          setPatients(responseData.content);
          setTotalPages(responseData.totalPages || 0);
          setTotalElements(responseData.totalElements || 0);
        } else {
          setPatients([]);
          setTotalPages(0);
          setTotalElements(0);
        }
      } else {
        setPatients([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
      setError("Failed to load patients. Please try again.");
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };
  

  const filteredPatients = patients.filter((patient) => {

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        (patient.name || "").toLowerCase().includes(searchLower) ||
        (patient.patientId || "").toLowerCase().includes(searchLower) ||
        (patient.mobileNo || "").toLowerCase().includes(searchLower) ||
        (patient.age || "").toString().toLowerCase().includes(searchLower) ||
        (patient.gender || "").toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    if (userRole === "Hospital" && selectedProblemType) {
      return true;
    }

    if (userRole === "Hospital" && selectedTaluk) {
      if (!patient.ashaBlockDetails?.phcDetails?.talukDetails || 
          patient.ashaBlockDetails.phcDetails.talukDetails.id != selectedTaluk) {
        return false;
      }
    }

    if (userRole === "Hospital" && selectedPhc) {
      if (!patient.ashaBlockDetails?.phcDetails || 
          patient.ashaBlockDetails.phcDetails.id != selectedPhc) {
        return false;
      }
    }
    
    if (selectedAshaBlock) {
      if (!patient.ashaBlockDetails || patient.ashaBlockDetails.id != selectedAshaBlock) {
        return false;
      }
    }
    
    if (selectedAsha) {
      if (!patient.ashaDetails || patient.ashaDetails.id != selectedAsha) {
        return false;
      }
    }
    
    return true;
  });
  
  const handlePageChange = (page) => {
    setCurrentPage(page - 1); 
  };

  const handleViewDetails = (patientId) => {
    navigate(`/patient-details/${patientId}`);
  };

  // Download to Excel functionality
  const handleDownloadExcel = () => {
    try {
      // Prepare data for Excel export - exclude primaryComplaints, include only basic details
      const exportData = filteredPatients.map((patient, index) => ({
        'Sl No': currentPage * pageSize + index + 1,
        'Name': patient.name || 'N/A',
        'Patient ID': patient.patientId || 'N/A',
        'Mobile Number': patient.mobileNo || 'N/A',
        'Age': patient.age || 'N/A',
        'Gender': patient.gender || 'N/A',
        'Aadhar Number': patient.idNumber || 'N/A',
        'Address': patient.address || patient.village || 'N/A',
        'Insurance': patient.insurance || 'N/A',
        'Asha Name': patient.ashaDetails?.name || 'N/A',
        'Asha Block': patient.ashaBlockDetails?.ashaBlock || 'N/A',
        'PHC': patient.ashaBlockDetails?.phcDetails?.phc || 'N/A',
        'Taluk': patient.ashaBlockDetails?.phcDetails?.talukDetails?.taluk || 'N/A'
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 8 },   // Sl No
        { wch: 20 },  // Name
        { wch: 15 },  // Patient ID
        { wch: 15 },  // Mobile Number
        { wch: 8 },   // Age
        { wch: 10 },  // Gender
        { wch: 15 },  // Aadhar Number
        { wch: 30 },  // Address
        { wch: 15 },  // Insurance
        { wch: 20 },  // Asha Name
        { wch: 20 },  // Asha Block
        { wch: 20 },  // PHC
        { wch: 20 }   // Taluk
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Patients History');

      // Generate filename with current date and applied filters
      let filename = 'Patients_History';
      
      // Add filter information to filename
      if (userRole === "Hospital" && selectedProblemType) {
        filename += `_${selectedProblemType}`;
      }
      if (selectedTaluk) {
        const talukName = taluks.find(t => t.id == selectedTaluk)?.taluk || 'Taluk';
        filename += `_${talukName}`;
      }
      if (selectedPhc) {
        const phcName = phcs.find(p => p.id == selectedPhc)?.phc || 'PHC';
        filename += `_${phcName}`;
      }
      if (selectedAshaBlock) {
        const blockName = ashaBlocks.find(b => b.id == selectedAshaBlock)?.ashaBlock || 'Block';
        filename += `_${blockName}`;
      }
      if (selectedAsha) {
        const ashaName = ashas.find(a => a.id == selectedAsha)?.name || 'Asha';
        filename += `_${ashaName}`;
      }
      
      filename += `_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      // Show success message
      setModalData({
        title: 'Download Successful',
        message: `Patient data has been downloaded successfully as ${filename}`,
        variant: 'success'
      });
      setShowAlertModal(true);

    } catch (error) {
      console.error('Error downloading Excel file:', error);
      setModalData({
        title: 'Download Failed',
        message: 'Failed to download patient data. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };
  
  const renderFilters = () => {
    return (
      <Row className="mb-3">
        {/* Hospital filters */}
        {userRole === "Hospital" && (
          <>
            <Col>
              <FloatingLabel controlId="problemType" label="Problem Description" className="mt-3">
                <Form.Select
                  value={selectedProblemType}
                  onChange={(e) => setSelectedProblemType(e.target.value)}
                >
                  <option value="">All Problems</option>
                  <option value="cataract">Cataract</option>
                  <option value="glaucoma">Glaucoma</option>
                  <option value="dr">DR</option>
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel controlId="taluk" label="Taluk" className="mt-3">
                <Form.Select
                  value={selectedTaluk}
                  onChange={(e) => setSelectedTaluk(e.target.value)}
                >
                  <option value="">All Taluks</option>
                  {taluks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.taluk}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel controlId="phc" label="PHC" className="mt-3">
                <Form.Select
                  value={selectedPhc}
                  onChange={(e) => setSelectedPhc(e.target.value)}
                >
                  <option value="">All PHCs</option>
                  {phcs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.phc}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel controlId="ashaBlock" label="Asha Block" className="mt-3">
                <Form.Select
                  value={selectedAshaBlock}
                  onChange={(e) => setSelectedAshaBlock(e.target.value)}
                >
                  <option value="">All Asha Blocks</option>
                  {ashaBlocks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.ashaBlock}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          </>
        )}

        {/* Super Admin filters */}
        {userRole === "Super Admins" && (
          <>
            <Col>
              <FloatingLabel controlId="taluk" label="Taluk" className="mt-3">
                <Form.Select
                  value={selectedTaluk}
                  onChange={(e) => setSelectedTaluk(e.target.value)}
                >
                  <option value="">All Taluks</option>
                  {taluks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.taluk}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel controlId="phc" label="PHC" className="mt-3">
                <Form.Select
                  value={selectedPhc}
                  onChange={(e) => setSelectedPhc(e.target.value)}
                >
                  <option value="">All PHCs</option>
                  {phcs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.phc}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          </>
        )}
        
        {/* Taluk user filters */}
        {userRole === "Taluk" && (
          <Col>
            <FloatingLabel controlId="phc" label="PHC" className="mt-3">
              <Form.Select
                value={selectedPhc}
                onChange={(e) => setSelectedPhc(e.target.value)}
              >
                <option value="">All PHCs</option>
                {phcs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.phc}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
        )}
        
        {/* Asha Block filter for Super Admin, Taluk, and PHC users */}
        {(userRole === "Super Admins" || userRole === "Taluk" || userRole === "PHC") && (
          <Col>
            <FloatingLabel controlId="ashaBlock" label="Asha Block" className="mt-3">
              <Form.Select
                value={selectedAshaBlock}
                onChange={(e) => setSelectedAshaBlock(e.target.value)}
              >
                <option value="">All Asha Blocks</option>
                {ashaBlocks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.ashaBlock}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
        )}
        
        {/* Asha filter for all user types except Hospital (Hospital has it in their section) */}
        {userRole !== "Hospital" && (
          <Col>
            <FloatingLabel controlId="asha" label="Asha" className="mt-3">
              <Form.Select
                value={selectedAsha}
                onChange={(e) => setSelectedAsha(e.target.value)}
              >
                <option value="">All Ashas</option>
                {ashas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
        )}
        
        {/* Search bar */}
        <Col>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mt-3"
          />
        </Col>
      </Row>
    );
  };

  return (
    <div>
      <AppNav />
      <h1 className="list-style">Patient's History</h1>
      <div className="list-table-style">
        {/* Filters */}
        {renderFilters()}
        
        {/* Download Button - Show for all admin roles when there's data to download */}
        {(userRole === "Super Admins" || userRole === "Taluk" || userRole === "PHC" || userRole === "Asha Block" || userRole === "Hospital") && 
         filteredPatients.length > 0 && !loading && (
          <Row className="mb-3">
            <Col className="d-flex justify-content-end">
              <Button 
                variant="success" 
                onClick={handleDownloadExcel}
                className="d-flex align-items-center"
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download to Excel
              </Button>
            </Col>
          </Row>
        )}
        
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading patients...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
            <Button 
              variant="link" 
              onClick={fetchPatients} 
              className="p-0 ms-2"
              style={{ boxShadow: 'none' }}
            >
              Retry
            </Button>
          </div>
        ) : userRole === "Hospital" && !selectedProblemType ? (
          <div className="alert alert-info text-center" role="alert">
            <h5>Please select a Problem Description to view patients</h5>
            <p className="mb-0">Choose from Cataract, Glaucoma, or DR to load the patient list.</p>
          </div>
        ) : (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Sl no</th>
                  <th className="list-item-align-center">Name</th>
                  <th className="list-item-align-center">Patient ID</th>
                  <th className="list-item-align-center">Mobile no</th>
                  <th className="list-item-align-center">Age</th>
                  <th className="list-item-align-center">Gender</th>
                  <th className="list-item-align-center">View Details</th>
                  <th className="list-item-align-center">Update</th>
                </tr>
              </thead>
              <tbody>
                {filteredPatients.length > 0 ? (
                  filteredPatients.map((patient, index) => (
                    <tr key={patient.id || index}>
                      <td className="list-item-align-center">{currentPage * pageSize + index + 1}</td>
                      <td className="list-item-align-center">{patient.name || 'N/A'}</td>
                      <td className="list-item-align-center">{patient.patientId || 'N/A'}</td>
                      <td className="list-item-align-center">{patient.mobileNo || 'N/A'}</td>
                      <td className="list-item-align-center">{patient.age || 'N/A'}</td>
                      <td className="list-item-align-center">{patient.gender || 'N/A'}</td>
                      <td className="list-item-align-center">
                        <Button 
                          variant="link" 
                          onClick={() => handleViewDetails(patient.id)}
                          className="p-0"
                          style={{ boxShadow: 'none' }}
                        >
                          <FaEye style={{ color: '#05275a' }} />
                        </Button>
                      </td>
                      <td className="list-item-align-center">
                        <Link to={`/update-patient/${patient.id}${selectedProblemType ? `?pdType=${selectedProblemType}` : ''}`}>
                          <FontAwesomeIcon
                            icon={faEdit}
                            size="lg"
                            style={{ color: '#05275a' }}
                          />
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center p-4">
                      {searchTerm ? 'No patients found matching your search.' : 'No patients found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center my-3">
                <AppPagination
                  curPage={currentPage + 1} 
                  maxPage={totalPages}
                  changePage={handlePageChange}
                />
              </div>
            )}
            
            {/* Results info */}
            {totalElements > 0 && (
              <div className="text-center mt-3">
                <small className="text-muted">
                  Showing {filteredPatients.length} of {totalElements} patients
                </small>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Alert Modal */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={modalData.title}
        message={modalData.message}
        variant={modalData.variant || 'info'}
      />
    </div>
  );
}

export default PatientsHistory;
